import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { ButtonTooltipValueProps, useNotification, useBaseHistory } from 'spindl-shared-components';

import { LineNumberValueProps } from '../../atoms/LineNumber';
import SendEmailMenu from '../../molecules/SendEmailMenu';
import { PreviewStateEnum } from '../../organisms/Preview/types';
import { NOTIFICATION_DURATION } from '../../../lib/constants';
import { ButtonStateEnum } from '../../../lib/types';
import { isEmptyString, getLineCount } from '../../../lib/utils';
import { getTranslation, emailTemplateTooltipContent } from '../../../lib/reactUtils';
import { EmailSettingsContext } from '../../../modules/emailTemplates/EmailSettingsContext';
import { EmailTemplate, EmailTemplateStatus, EmailTemplateStatusType } from '../../../modules/emailTemplates/types';
import { getEmailTemplateStatus } from '../../../modules/emailTemplates/utils';

import { LeftPanelStateEnum, TemplateEditorPageCombinedProps } from './types';
import {
  getEmailTemplateHTMLUseCase,
  getEmailTemplateUseCase,
  updateEmailTemplateHTMLUseCase,
} from './TemplateEditorPage.interactor';

const usePresenter = (
  props: TemplateEditorPageCombinedProps,
): TemplateEditorPageCombinedProps => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const history = useBaseHistory();
  const { search } = useLocation();
  const { clear, trigger } = useNotification();
  const { templateId } = useParams<{ templateId: string }>();

  const splitView = t('template_editor.views.split_view');
  const codeView = t('template_editor.views.code_view');
  const preview = t('template_editor.views.preview');

  const [leftPanel, setLeftPanel] = useState<LeftPanelStateEnum>('Collapsed');
  const [showCloseSettingsSideBarModal, setShowCloseSettingsSideBarModal] =
    useState<boolean>(false);

  // Need post modal hook for either closing or not closing the side bar
  //    - close side bar if click x
  //    - don't close side bar if click exit
  const [postSettingsModal, setPostSettingsModal] = useState<() => void>(
    () => {},
  );

  const [showExitModal, setShowExitModal] = useState<boolean>(false);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [currentView, setCurrentView] = useState(splitView);
  const [saveButtonState, setSaveButtonState] = useState<ButtonStateEnum>('Default');
  const [disableSave, setDisableSave] = useState(false);
  const [showEmail, setShowEmail] = useState<{ show: boolean, overrideShow: boolean }>({
    show: false, 
    overrideShow: false,
  });
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const [emailButtonProps, setEmailButtonProps] = useState<ButtonTooltipValueProps>();

  const [htmlInputString, setHtmlInputString] = useState<string>('');
  const [previewState, setPreviewState] = useState<PreviewStateEnum>(
    isEmptyString(htmlInputString) ? 'Empty' : 'Active',
  );
  const [templateStatus, setTemplateStatus] = useState<EmailTemplateStatus>();

  const inputFile = useRef<HTMLInputElement | null>(null);

  const {
    isUnsaved: settingsIsUnsaved,
    saveSettings,
    discardChanges,
    emailSettings: emailSettingsUnsaved,
    emailSettingsError,
    isSettingsFormValid,
  } = useContext(EmailSettingsContext);

  // Get template and template content
  const { data: emailTemplate, isLoading: isEmailTemplateLoading } = useQuery(
    ['getEmailTemplate', templateId],
    () => getEmailTemplateUseCase(templateId),
  );
  // The query will only be enabled when emailTemplate.id is defined, so we can safely use the non-null assertion
  const { data, isSuccess, isLoading: isHtmlLoading } = useQuery(
    ['getEmailTemplateHTML', emailTemplate?.id],
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    () => getEmailTemplateHTMLUseCase(emailTemplate!.id), {
      enabled: !!emailTemplate && !!emailTemplate.id,
    },
  );

  // Update template content mutation
  const updateHTMLMutation = useMutation(
    (file: string) => {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      return updateEmailTemplateHTMLUseCase(emailTemplate!.id, file);
    },
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries('getEmailTemplateHTML');
      },
    },
  );

  const mapQueryToView = useCallback((view: string | null) => {
    switch (view) {
      case 'codeView':
        setCurrentView(codeView);
        break;
      case 'splitView':
        setCurrentView(splitView);
        break;
      case 'preview':
        setCurrentView(preview);
        break;
      default:
        history.replace({
          search: '?view=splitView',
        });
        break;
    }
  }, [codeView, splitView, preview, history]);
  
  useEffect(() => {
    const query = new URLSearchParams(search);
    const view = query.get('view');
    mapQueryToView(view);
  }, [search, mapQueryToView]);

  useEffect(() => {
    clear();
  }, []);

  useEffect(() => {
    setIsLoading(isEmailTemplateLoading || isHtmlLoading);
  }, [isEmailTemplateLoading, isHtmlLoading]);

  useEffect(() => {
    setPreviewState(isEmptyString(htmlInputString) ? 'Empty' : 'Active');
    setSaveButtonState('Default');
    setDisableSave(htmlInputString === data);
  }, [htmlInputString, previewState, data]);

  useEffect(() => {
    if (isSuccess) {
      setHtmlInputString(data);
    }
  }, [data, isSuccess]);

  const checkTemplateStatus = useCallback((template: EmailTemplate) => {
    return getEmailTemplateStatus(
      EmailTemplateStatusType.subheader, 
      template, 
      htmlInputString,
      emailSettingsUnsaved, 
      emailSettingsError, 
      settingsIsUnsaved,
    );
  }, [htmlInputString, emailSettingsError, emailSettingsUnsaved, settingsIsUnsaved]);

  //use this for template status
  useEffect(() => {
    if (emailTemplate && !isLoading) {
      setTemplateStatus(checkTemplateStatus(emailTemplate));
    }
  }, [htmlInputString, emailSettingsUnsaved, emailSettingsError, emailTemplate, isLoading, checkTemplateStatus]);

  const lineNumbers: LineNumberValueProps[] = [];
  for (let i = 1; i < getLineCount(htmlInputString) + 1; i++) {
    lineNumbers.push({
      text: {
        value: i,
      },
    });
  }

  const exitTemplateEditorPage = () => {
    history.push('/');
  };

  const saveTemplateContent = useCallback(async () => {
    setSaveButtonState('Loading');
    setDisableSave(true);
    if (emailTemplate?.id) {
      await updateHTMLMutation.mutateAsync(htmlInputString);
    }
    setSaveButtonState('Loaded');
    setDisableSave(false);
  }, [htmlInputString]);

  const exitWithoutSave = () => {
    setShowExitModal(false);
    exitTemplateEditorPage();
  };

  const replaceContent = () => {
    if (inputFile && inputFile.current) {
      inputFile.current.click();
    }
    setShowModal(false);
  };

  const handleFileChanges = (event) => {
    const file = event.target.files[0];
    const reader = new FileReader();
    reader.onload = function () {
      setHtmlInputString(reader.result as string);
    };
    reader.readAsText(file as Blob);
    event.target.value = '';
  };

  const saveAndExit = async () => {
    setShowExitModal(false);
    await saveTemplateContent();
    exitTemplateEditorPage();
    trigger({
      message: getTranslation('template_editor.exit_modal.confirm_saved'),
      type: 'Positive',
      duration: NOTIFICATION_DURATION,
    });
  };

  const importHTML = () => {
    if (htmlInputString) {
      setShowModal(true);
    } else if (inputFile && inputFile.current) {
      inputFile.current.click();
    }
  };

  const exitTemplate = () => {
    if (leftPanel === 'Settings') {
      if (settingsIsUnsaved) {
        if (htmlInputString === data) {
          setPostSettingsModal(() => exitTemplateEditorPage);
        }
        return setShowCloseSettingsSideBarModal(true);
      }
    }

    if (htmlInputString !== data) {
      setPostSettingsModal(() => exitTemplateEditorPage);
      setShowExitModal(true);
    } else {
      exitTemplateEditorPage();
    }
  };

  const saveTemplate = async () => {
    await saveTemplateContent();
  };

  const closeSidebar = () => {
    setLeftPanel('Collapsed');
  };

  const onCloseSettings = (): boolean => {
    if (!settingsIsUnsaved) {
      closeSidebar();
      return true;
    }

    // Else there are unsaved changes
    setShowCloseSettingsSideBarModal(true);
    setPostSettingsModal(() => closeSidebar);
    return true;
  };

  // Settings warning modal button handlers
  const saveSettingsModal = async () => {
    setShowCloseSettingsSideBarModal(false);
    
    if (isSettingsFormValid(true) && await saveSettings()) {
      closeSidebar();
    }

    setPostSettingsModal(() => {});
  };

  const discardSettingsModal = () => {
    discardChanges();
    setShowCloseSettingsSideBarModal(false);

    postSettingsModal();
    setPostSettingsModal(() => {});
  };

  const showTestEmail = (show: boolean) => {
    setShowEmail({
      show: show,
      overrideShow: true,
    });
  };
  
  const sendEmailOverlay = useCallback(() => {
    const testEmailMenu = {
      emailTemplate,
      html: htmlInputString,
      setShow: showTestEmail,
      textField: {
        label: {
          value: t('template_editor.test_email_menu.title'),
        },
        textInput: {
          textPlaceholder: t('template_editor.test_email_menu.text_placeholder'),
        },
        text: {
        },
      },
      button: {
        text: {
          value: t('template_editor.test_email_menu.button'),
        },
      },
    };
    
    return (
      <SendEmailMenu {...testEmailMenu}/> 
    );
  }, [emailTemplate, htmlInputString, t, saveTemplateContent]);

  useEffect(() => {
    let emailButton: ButtonTooltipValueProps = {
      targetProps: {
        button: {
          icon: {
            asset: 'EmailOutline',
          },
          text: {
            value: t('template_editor.test_email'),
          },
        },
      },
      contentProps: {
        align: 'Right',
        content: sendEmailOverlay(),
        constrainWidth: false,
      },
      placement: 'bottom-end',
    };

    if (!showEmail.overrideShow) {
      emailButton = {
        ...emailButton,
        action: 'click',
      };
    } else {
      emailButton = {
        ...emailButton,
        show: showEmail.show,
      };

      setShowEmail({
        ...showEmail, 
        overrideShow: false,
      });
    }

    setEmailButtonProps(emailButton);
  }, [showEmail, htmlInputString, emailTemplate, t, sendEmailOverlay]);

  const tooltipContent = (errors?: React.ReactNode[]): JSX.Element => {
    return emailTemplateTooltipContent(errors);
  };

  props = {
    ...props,
    templateEditorSubNav: {
      error: templateStatus?.errorMessages.length ? 'Error' : 'None',
      styleType: isLoading ? 'Loading' : 'Default',
      button: {
        icon: {
          asset: 'Back',
        },
        text: {
          value: t('template_editor.exit_button'),
        },
        onClick: exitTemplate,
      },
      errorIndicator: {
        action: 'hover',
        targetProps: {
          icon: {
            asset: 'Error',
          },
          text: {
            value: t('template_editor.incomplete_template'),
          },
          dashUnderline: {
          },
        },
        contentProps: {
          align: 'Left',
          content: tooltipContent(templateStatus?.errorMessages),
        },
        placement: 'bottom-start',
        offset: [40, 0],
      },
      pillDropDown: {
        toggleProps: {
          text: {
            value: currentView,
          },
        },
        menuProps: {
          dropDownItems: [
            {
              type: 'DefaultIconText',
              text: {
                value: splitView,
              },
              icon: {
                asset: 'SplitView',
                colour: 'IconDark',
              },
              onClick: () => {
                history.replace({
                  search: '?view=splitView',
                });
              },
              state: currentView === splitView ? 'Selected' : 'Unselected',
            },
            {
              type: 'DefaultIconText',
              text: {
                value: codeView,
              },
              icon: {
                asset: 'CodeView',
                colour: 'IconDark',
              },
              onClick: () => {
                history.replace({
                  search: '?view=codeView',
                });
              },
              state: currentView === codeView ? 'Selected' : 'Unselected',
            },
            {
              type: 'DefaultIconText',
              text: {
                value: preview,
              },
              icon: {
                asset: 'Preview',
                colour: 'IconDark',
              },
              onClick: () => {
                history.replace({
                  search: '?view=preview',
                });
              },
              state: currentView === preview ? 'Selected' : 'Unselected',
            },
          ],
        },
      },
      buttonTooltip: emailButtonProps,
      saveButton: {
        text: {
          value: saveButtonState === 'Loaded' ? t('template_editor.saved_button') : t('template_editor.save_button'),
        },
        state: saveButtonState,
        disabled: disableSave,
        onClick: saveTemplate,
      },
    },
    hTMLEditor: {
      hTMLEditorHeader: {
        labelText: {
          value: emailTemplate?.name,
        },
        button: {
          icon: {
            asset: 'Import',
          },
          text: {
            value: t('template_editor.html_editor.import_html'),
          },
          onClick: importHTML,
        },
      },
      codeSpace: {
        hTMLTextArea: {
          lineNumberList: {
            lineNumbers: lineNumbers,
          },
          textArea: {
            textValue: htmlInputString,
          },
        },
        updateHtmlFunction: (updatedHtml) => setHtmlInputString(updatedHtml),
      },
    },
    preview: {
      state: previewState,
      iframeSrcDoc: htmlInputString,
      title: {
        value: t('template_editor.preview.empty_template.title'),
      },
      instructions: {
        value: t('template_editor.preview.empty_template.instructions'),
      },
    },
    editorTabList: {
      editorTabItems: [
        {
          onClick: () => setLeftPanel('Tags'),
          icon: {
            asset: 'Tag',
          },
          state: leftPanel === 'Tags' ? 'Selected' : 'Unselected',
        },
        {
          onClick: () => setLeftPanel('Settings'),
          icon: {
            asset: 'Settings',
          },
          state: leftPanel === 'Settings' ? 'Selected' : 'Unselected',
        },
      ],
    },
    inputFile,
    handleFileChanges,
    warningModal: {
      modalProps: {
        type: 'Default',
        styleType: 'Default',
        title: {
          value: t('template_editor.warning_modal.title'),
        },
        text: {
          value: t('template_editor.warning_modal.content'),
        },
        actionButton: {
          text: {
            value: t('template_editor.warning_modal.continue_button'),
          },
          onClick: replaceContent,
        },
        cancelButton: {
          text: {
            value: t('template_editor.warning_modal.cancel_button'),
          },
          onClick: () => setShowModal(false),
        },
        button: {
          onClick: exitTemplate,
        },
      },
      show: showModal,
    },
    exitModal: {
      modalProps: {
        type: 'Default',
        styleType: 'Default',
        title: {
          value: t('template_editor.exit_modal.title'),
        },
        text: {
          value: t('template_editor.exit_modal.content'),
        },
        actionButton: {
          text: {
            value: t('template_editor.exit_modal.save_button'),
          },
          onClick: saveAndExit,
        },
        cancelButton: {
          text: {
            value: t('template_editor.exit_modal.exit_button'),
          },
          onClick: exitWithoutSave,
        },
        button: {
          onClick: () => setShowExitModal(false),
        },
      },
      show: showExitModal,
    },
    settingsSideBar: {
      emailTemplate,
      button: {
        onClick: onCloseSettings,
      },
    },
    closeSettingsSideBarModal: {
      modalProps: {
        type: 'Default',
        styleType: 'Default',
        title: {
          value: t('side_bar.settings.warning_modal.title'),
        },
        text: {
          value: t('side_bar.settings.warning_modal.content'),
        },
        actionButton: {
          text: {
            value: t('side_bar.settings.warning_modal.continue_button'),
          },
          onClick: saveSettingsModal,
        },
        cancelButton: {
          text: {
            value: t('side_bar.settings.warning_modal.cancel_button'),
          },
          onClick: discardSettingsModal,
        },
        button: {
          onClick: () => setShowCloseSettingsSideBarModal(false),
        },
      },
      show: showCloseSettingsSideBarModal,
    },
    tagsSideBar: {
      button: {
        onClick: () => setLeftPanel('Collapsed'),
      },
      emailTemplate,
    },
    currentView,
    leftPanel,
  };

  return props;
};

export default usePresenter;
